/**
 * E34 · DEFAULT HOMEWORK TITLES — founder ruling Q-ru-3 A (2026-10-24): "save a title only when the therapist edits it; an unedited
 * default is shown in each reader's own language."
 *
 * A tool's own Assign button (the portal's tool sections) starts the title from that tool's default. Left as it is, the assignment
 * carries NO title (`HomeworkAssigned.title: null`) — and, for a default that quotes the behavior (functional analysis), the
 * behavior's name as `titleName`. Every reader renders the default from i18n in ITS language: the phone (the client's), the portal
 * (the therapist's), the push and the e-mail (the recipient's `users.lng`). A title the therapist wrote or edited is stored and
 * shown as written. Facts written before the ruling keep the title they have.
 *
 * This table is the ONE place a default title is named: the portal's presets read their text from it, the decider asks it whether
 * a target HAS a default (`HOMEWORK_TITLE_REQUIRED` otherwise), the API renders through `homeworkTitleOf`. Every key lives in
 * `translationEn` / `translationRu` (the API's resources; the portal's spread them) — `defaultTitles.spec.ts` (@cc/domain) pins it.
 */
export const HOMEWORK_DEFAULT_TITLE_KEYS = {
  // E36 · Values
  valuesSort: 'values.hub.assign.defaultTitle.valuesSort',
  valuesWheel: 'values.hub.assign.defaultTitle.valuesWheel',
  valuedLivingQuestionnaire: 'values.hub.assign.defaultTitle.valuedLivingQuestionnaire',
  // E53 · the wind rose (assigned from the Values section)
  windRose: 'values.hub.assign.defaultTitle.windRose',
  // E47 · Schema modes
  modeCheckIn: 'modes.hub.assign.defaultTitle',
  // E45 · the sleep diary
  sleepDiary: 'sleep.hub.assign.defaultTitle',
  // E41 · Behavioral activation
  activityMonitoring: 'ba.hub.assign.defaultTitle.activityMonitoring',
  activityRanking: 'ba.hub.assign.defaultTitle.activityRanking',
  activityPlanning: 'ba.hub.assign.defaultTitle.activityPlanning',
  supportContract: 'ba.hub.assign.defaultTitle.supportContract',
  // E49 · Motivation
  changeRating: 'mi.hub.assign.defaultTitle.changeRating',
  decisionalBalance: 'mi.hub.assign.defaultTitle.decisionalBalance',
  changePlan: 'mi.hub.assign.defaultTitle.changePlan',
  // E48 · Functional analysis — the default quotes the behavior: `{{name}}`
  chainAnalysis: 'fa.assign.defaultTitle',
} as const;

export type HomeworkDefaultTitleTarget = keyof typeof HOMEWORK_DEFAULT_TITLE_KEYS;

/** The targets whose default title quotes a name (`{{name}}` — the behavior the records are for). */
export const HOMEWORK_TITLE_NAME_TARGETS: readonly string[] = ['chainAnalysis'];

/** An assignment's target, as far as a title needs it (the fact's `HomeworkTarget`). */
export type HomeworkTitleTarget = {
  type?: string | null;
  ref?: string | null;
  version?: number | null;
  source?: string | null;
};

/** What a reader needs to name an assignment — every homework read model row carries it. */
export type HomeworkTitled = {
  kind?: string | null;
  target?: HomeworkTitleTarget | null;
  /** The therapist's own title; null (or absent) = the target's default. */
  title?: string | null;
  /** The name the default quotes (functional analysis: the behavior); null otherwise. */
  titleName?: string | null;
};

/** A translator fixed to the reader's language (i18next's `TFunction` qualifies). */
export type HomeworkTitleTranslate = (key: string, options?: Record<string, unknown>) => string;

const hasOwn = (key: string): key is HomeworkDefaultTitleTarget =>
  Object.prototype.hasOwnProperty.call(HOMEWORK_DEFAULT_TITLE_KEYS, key);

/** The i18n key of a target's default title — only a TOOL has one (a task, a worksheet, a link are always titled). */
export const homeworkDefaultTitleKey = (
  kind: string | null | undefined,
  target: HomeworkTitleTarget | null | undefined,
): string | null => {
  const type = target?.type;
  return kind === 'tool' && type && hasOwn(type) ? HOMEWORK_DEFAULT_TITLE_KEYS[type] : null;
};

/** Does this target's default title quote a name (`titleName`)? */
export const homeworkTitleQuotesName = (
  kind: string | null | undefined,
  target: HomeworkTitleTarget | null | undefined,
): boolean =>
  homeworkDefaultTitleKey(kind, target) !== null && HOMEWORK_TITLE_NAME_TARGETS.includes(target?.type ?? '');

/** The values the default is rendered with — never HTML-escaped (a push, a phone row and an e-mail each escape their own way). */
const titleOptions = (name: string | null | undefined) => ({
  name: name ?? '',
  interpolation: { escapeValue: false },
});

/**
 * The assignment's title for ONE reader: the therapist's own title as written, else the target's default rendered with the
 * reader's translator. A row with neither (no title, a target without a default — never written by the decider) reads ''.
 */
export const homeworkTitleOf = (row: HomeworkTitled, t: HomeworkTitleTranslate): string => {
  if (row.title && row.title.trim()) return row.title;
  const key = homeworkDefaultTitleKey(row.kind, row.target);
  return key ? t(key, titleOptions(row.titleName)) : row.title ?? '';
};

const NAME_SLOT = '\u0000';

/**
 * Is a typed title the target's default in one of the given languages? `{titleName}` when it is (the name read back out of a
 * default that quotes one), null when it is the therapist's own. The API's net for a client that still sends the default's TEXT
 * (a portal bundle from before the ruling, another caller): a text equal to a shipped default is not an edit.
 */
export const matchHomeworkDefaultTitle = (
  kind: string | null | undefined,
  target: HomeworkTitleTarget | null | undefined,
  title: string,
  translators: readonly HomeworkTitleTranslate[],
): { titleName: string | null } | null => {
  const key = homeworkDefaultTitleKey(kind, target);
  const typed = title.trim();
  if (!key || !typed) return null;
  const quotes = homeworkTitleQuotesName(kind, target);
  for (const t of translators) {
    if (!quotes) {
      if (typed === t(key, titleOptions(null)).trim()) return { titleName: null };
      continue;
    }
    const [before = '', after = ''] = t(key, titleOptions(NAME_SLOT)).split(NAME_SLOT);
    if (typed.length > before.trim().length + after.trim().length && typed.startsWith(before.trim()) && typed.endsWith(after.trim())) {
      const name = typed.slice(before.trim().length, typed.length - after.trim().length).trim();
      if (name) return { titleName: name };
    }
  }
  return null;
};
